/**
 * SHIP-EXPANSION: the faction model. Pure data — engine-agnostic (no Three.js, no
 * DOM, no Math.random, no Date.now). Eight in-fiction powers + cross-cutting
 * independents (ship-expansion.md §"Faction Visual Doctrines"). This is the
 * canonical FactionId set; the legacy display-string flavor tags in signal.ts
 * (`FACTIONS`, used for encounter/bounty labels) reconcile to these display names
 * via FACTION_DISPLAY — the five Powers' names match the legacy strings verbatim,
 * so the reconciliation is loss-free and changes no existing behaviour.
 */

/** Canonical faction identifier. `quiet` ships are never ambient-spawnable. */
export type FactionId =
  | 'guild' // Guild of the Cenotaph — memorial keepers, civilian-rugged surveyors
  | 'concord' // Concord of Sol (Terran High Guard) — brutalist-minimalist white hulls
  | 'aresian' // Aresian Republic (Mars) — industrial, attritional, iron-oxide
  | 'belt' // Spún Compact (Belt Clans) — asymmetric patchwork scavengers
  | 'jovian' // Jovian Concession (Galilei-Vance) — corporate, lacquered black
  | 'outer' // Outer Concordance — monastic-scientific, ice-white, antenna-clad
  | 'quiet' // The Quiet — alien/unknown obsidian entities (never ambient)
  | 'independent'; // unaffiliated couriers + freelancers

/** Every FactionId, declaration order = canonical order. */
export const FACTION_IDS: readonly FactionId[] = [
  'guild',
  'concord',
  'aresian',
  'belt',
  'jovian',
  'outer',
  'quiet',
  'independent',
] as const;

/**
 * Canonical display name per faction. The five Powers' names (concord/aresian/
 * belt/jovian/outer) are byte-identical to signal.ts's legacy FACTIONS flavor
 * strings so the reconciliation in signal.ts produces the same values it always
 * had (factionFor output is unchanged).
 */
export const FACTION_DISPLAY: Record<FactionId, string> = {
  guild: 'Guild of the Cenotaph',
  concord: 'Concord of Sol',
  aresian: 'Aresian Republic',
  belt: 'Spún Compact',
  jovian: 'Jovian Concession',
  outer: 'Outer Concordance',
  quiet: 'The Quiet',
  independent: 'Independent',
};

/** Display name for a faction (HUD/labels). */
export const factionDisplayName = (id: FactionId): string => FACTION_DISPLAY[id];

/** Type-guard: is an arbitrary string a known FactionId? */
export const isFactionId = (v: string): v is FactionId =>
  (FACTION_IDS as readonly string[]).includes(v);

/**
 * SHIP-EXPANSION (save v5): per-faction reputation. 0 = neutral; positive =
 * friendly, negative = hostile. Persisted in the SaveState and read by the
 * economy's light standing helper for future purchase gating.
 */
export type FactionStanding = Record<FactionId, number>;

/** Neutral standing toward every faction (all 0). */
export const neutralFactionStanding = (): FactionStanding =>
  Object.fromEntries(FACTION_IDS.map((f) => [f, 0])) as FactionStanding;
